import React, { useEffect, useState } from "react";
import { api, trackView } from "../lib/api";
import SEO from "../components/SEO";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const services = [
  "Site vitrine professionnel",
  "Site avec prise de rendez-vous",
  "Site avec Click & Collect",
  "Click & Collect + Prise de RDV",
  "Refonte de site existant",
  "Identité visuelle (logo, charte)",
  "Pack identité visuelle complet",
  "Autre / projet sur-mesure",
];
const industries = [
  "Boulangerie / Pâtisserie", "Agence immobilière", "Barber / Coiffeur",
  "Fleuriste", "Salle de sport", "Artisan", "Commerce local", "Autre",
];
const budgets = ["< 1 500 €", "1 500 – 3 000 €", "3 000 – 6 000 €", "6 000 € +", "À discuter"];

export default function Devis() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    service: "", industry: "", budget: "",
    name: "", email: "", phone: "", company: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { trackView("/devis"); }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const next = () => {
    if (step === 1 && !form.service) return toast.error("Sélectionnez un service");
    if (step === 2 && !form.industry) return toast.error("Sélectionnez un secteur");
    setStep(step + 1);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      return toast.error("Merci de remplir tous les champs requis");
    }
    setSubmitting(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
    } catch {
      toast.error("Une erreur est survenue. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="pt-32 pb-24 bg-[#09090b] text-white min-h-screen flex items-center">
        <div className="max-w-xl mx-auto text-center px-6">
          <div className="w-16 h-16 bg-[#d4af37]/10 border border-[#d4af37] rounded-full mx-auto flex items-center justify-center mb-8">
            <Check size={28} className="text-[#d4af37]" />
          </div>
          <h1 className="font-display text-4xl mb-4" data-testid="devis-success">Demande reçue.</h1>
          <p className="text-zinc-400 font-light leading-relaxed">
            Merci {form.name}. Nous revenons vers vous sous 24 heures à l'adresse {form.email}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 bg-[#09090b] text-white min-h-screen">
      <SEO
        title="Devis gratuit — Blackline Studio"
        description="Demandez un devis gratuit pour votre site internet, Click & Collect, prise de rendez-vous ou identité visuelle. Réponse sous 24 heures."
        keywords="devis site internet, devis création site web, devis identité visuelle, devis Click and Collect"
      />
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">Devis</div>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight leading-[1.02] mb-6" data-testid="devis-title">
          Parlons de votre <span className="italic font-light">projet</span>.
        </h1>
        <p className="text-zinc-400 font-light mb-12 max-w-xl">
          Quelques questions pour préparer notre échange. Réponse sous 24 h.
        </p>

        {/* Stepper */}
        <div className="flex gap-2 mb-12" data-testid="stepper">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1 flex-1 transition-colors ${step >= s ? "bg-[#d4af37]" : "bg-white/10"}`} />
          ))}
        </div>

        <form onSubmit={submit} className="space-y-8">
          {step === 1 && (
            <div data-testid="step-1">
              <label className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4 block">Étape 1 — Quel service ?</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10">
                {services.map((s, i) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => setForm({ ...form, service: s })}
                    data-testid={`service-option-${i}`}
                    className={`bg-[#0a0a0c] p-5 text-left transition-all text-sm ${
                      form.service === s ? "bg-[#d4af37]/10 text-white border-l-2 border-[#d4af37]" : "text-zinc-300 hover:bg-[#101013]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-2">
              <label className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4 block">Étape 2 — Votre secteur</label>
              <div className="grid grid-cols-2 gap-px bg-white/10 mb-8">
                {industries.map((s, i) => (
                  <button
                    type="button" key={s}
                    onClick={() => setForm({ ...form, industry: s })}
                    data-testid={`industry-option-${i}`}
                    className={`bg-[#0a0a0c] p-5 text-left transition-all text-sm ${
                      form.industry === s ? "bg-[#d4af37]/10 text-white border-l-2 border-[#d4af37]" : "text-zinc-300 hover:bg-[#101013]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <label className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4 block">Budget envisagé (optionnel)</label>
              <div className="flex flex-wrap gap-2">
                {budgets.map((b, i) => (
                  <button
                    type="button" key={b}
                    onClick={() => setForm({ ...form, budget: b })}
                    data-testid={`budget-option-${i}`}
                    className={`px-4 py-2 text-sm border transition-all ${
                      form.budget === b ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/5" : "border-white/10 text-zinc-400 hover:border-white/30"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-3" className="space-y-5">
              <label className="text-xs tracking-[0.2em] uppercase text-zinc-500 block">Étape 3 — Vos coordonnées</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input required placeholder="Nom complet *" value={form.name} onChange={update("name")} data-testid="input-name"
                  className="bg-[#0a0a0c] border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:border-[#d4af37] outline-none transition-colors" />
                <input required type="email" placeholder="Email *" value={form.email} onChange={update("email")} data-testid="input-email"
                  className="bg-[#0a0a0c] border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:border-[#d4af37] outline-none transition-colors" />
                <input placeholder="Téléphone" value={form.phone} onChange={update("phone")} data-testid="input-phone"
                  className="bg-[#0a0a0c] border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:border-[#d4af37] outline-none transition-colors" />
                <input placeholder="Entreprise" value={form.company} onChange={update("company")} data-testid="input-company"
                  className="bg-[#0a0a0c] border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:border-[#d4af37] outline-none transition-colors" />
              </div>
              <textarea required rows={5} placeholder="Décrivez votre projet *" value={form.message} onChange={update("message")} data-testid="input-message"
                className="w-full bg-[#0a0a0c] border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:border-[#d4af37] outline-none transition-colors resize-none" />
            </div>
          )}

          <div className="flex items-center justify-between pt-4">
            {step > 1 ? (
              <button type="button" onClick={() => setStep(step - 1)} data-testid="step-back"
                className="text-sm text-zinc-400 hover:text-white">← Retour</button>
            ) : <div />}
            {step < 3 ? (
              <button type="button" onClick={next} data-testid="step-next"
                className="btn-gold px-7 py-3.5 inline-flex items-center gap-2 text-sm">
                Continuer <ArrowRight size={16} />
              </button>
            ) : (
              <button type="submit" disabled={submitting} data-testid="submit-quote"
                className="btn-gold px-7 py-3.5 inline-flex items-center gap-2 text-sm disabled:opacity-60">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                {submitting ? "Envoi…" : "Envoyer ma demande"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
